import * as Sentry from '@sentry/vue';
import {
  createI18n,
} from 'vue-i18n';

import de from '@/constants/locales/de';
import en from '@/constants/locales/en';
import pl from '@/constants/locales/pl';
import sk from '@/constants/locales/sk';
import {
  TSupportedLocale,
} from '@/types/i18n.ts';

// Type-define "en" as the master schema for the resource
// "master" schema means it will be used for keys resolving
type MessageSchema = typeof en;

export const messages = {
  en,
  de,
  pl,
  sk,
};

// user can put "?trl=true" in URL to debug trl keys
const urlParams = new URLSearchParams(window.location.search);

const i18nDefaultSettings = {
  locale: 'en',
  fallbackLocale: 'en',
  // cleanup 3 places and remove
  globalInjection: true,
  messages: urlParams.get('trl') ? {} as typeof messages : messages,
};

// Dedupe within a session so the same missing key/locale pair is only reported to Sentry once,
// no matter how many times it gets rendered.
const reportedMissingTranslationKeys = new Set<string>();

function reportMissingTranslation(locale: string, key: string, type?: string): void {
  // 'translate exists' fires from `te()` existence checks, which intentionally probe for keys
  // that may not exist - only actual `t()`/`$t()` renders indicate a real missing translation.
  if (type !== 'translate') {
    return;
  }

  const reportKey = `${locale}|${key}`;
  if (reportedMissingTranslationKeys.has(reportKey)) {
    return;
  }
  reportedMissingTranslationKeys.add(reportKey);

  Sentry.captureMessage(`Missing translation: ${key} (${locale})`, {
    level: 'warning',
    // one fingerprint per key/locale pair so each missing translation becomes its own Sentry issue,
    // instead of every occurrence being grouped into a single generic issue
    fingerprint: [
      'missing-translation',
      locale,
      key,
    ],
    extra: {
      locale,
      key,
    },
  });
}

type TPluralRule = (choice: number, choicesLength: number, defaultPluralRule?: TPluralRule) => number;

/**
 * Polish messages with three forms are written as "one | few | many", e.g. "Godzina | Godziny | Godzin",
 * while the default rule reads three forms as "zero | one | many" and finds no form for fractions.
 */
export const polishPluralRule: TPluralRule = (choice, choicesLength, defaultPluralRule) => {
  if (choicesLength !== 3) {
    return defaultPluralRule?.(choice, choicesLength) ?? 0;
  }
  const count = Math.abs(choice);
  if (!Number.isInteger(count)) {
    return 1;
  }
  if (count === 1) {
    return 0;
  }
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  const isFewForm = lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14);
  return isFewForm ? 1 : 2;
};

export const i18n = createI18n<[MessageSchema], TSupportedLocale, false>({
  ...i18nDefaultSettings,
  pluralRules: {
    pl: polishPluralRule,
  },
  legacy: false,
  missing: (locale, key, _instance, type) => reportMissingTranslation(locale, key, type),
});
